import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import colors from 'resources/colors';
import msgId from 'resources/intl';
import { Ship } from 'models/ships';
import { Box, Radio, SxProps, Typography } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { ContentsHeader } from 'components/ContentsHeader';
import dimens from 'resources/dimens';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CloseIconButton,
  SubDrawerBox,
} from 'components/subDrawer';
import { SearchBar } from 'components/SearchBar';
import { theme } from 'resources/theme';
import { CustomScrollBars } from 'components/CustomScrollBars';
import { NormalButton } from 'components/NormalButton';

interface ShipDrawerProps {
  sx?: SxProps;
  ships: Ship[];
  open?: boolean;
  onChangeOpen?: (open: boolean) => void;
  onSearchInputChange?: (input: string) => void;
  scrollvbarMargin?: number;
  onChangeShip?: (shipId: string) => void;
  selectedShipId?: string;
  onOkClick?: () => void;
}

export function ShipDrawer(props: ShipDrawerProps): JSX.Element {
  const {
    sx,
    ships,
    open,
    onChangeOpen,
    onSearchInputChange,
    scrollvbarMargin,
    onChangeShip,
    selectedShipId,
    onOkClick,
  } = props;
  const sbMargin = scrollvbarMargin ? scrollvbarMargin : 0;
  const intl = useIntl();
  const [searchValue, setSearchValue] = useState<string>('');

  const handleSeatchInputChange = (input: string) => {
    setSearchValue(input);
    onSearchInputChange && onSearchInputChange(input);
  };

  return (
    <SubDrawerBox
      sx={{
        width: open ? dimens.subDrawer.width : dimens.subDrawer.minWidth,
        minWidth: open ? dimens.subDrawer.width : dimens.subDrawer.minWidth,
        ...sx,
      }}
    >
      {open && (
        <React.Fragment>
          <ContentsHeader title={intl.formatMessage({ id: msgId.shipSelection })}>
            {onChangeOpen != null && (
              <CloseIconButton onClick={() => onChangeOpen(false)}>
                <ChevronLeftIcon />
              </CloseIconButton>
            )}
          </ContentsHeader>
          <SearchBar
            sx={{
              marginTop: theme.spacing(0.5),
              width: dimens.subDrawer.width,
              padding: '4px 8px 4px 8px',
            }}
            placeholder={intl.formatMessage({ id: msgId.shipNameSearch })}
            value={searchValue}
            onChange={(event) => handleSeatchInputChange(event.target.value)}
          />
          <CustomScrollBars height={`calc(100% - ${sbMargin + 44}px)`}>
            {ships.length > 0 &&
              ships.map((ship) => {
                const isSelected = selectedShipId === ship.shipId;

                return (
                  <Box
                    key={ship.shipId}
                    sx={{
                      backgroundColor: isSelected
                        ? colors.accordion.header
                        : colors.accordion.header,
                      borderTop: `1px solid ${colors.accordion.border}`,
                      ':last-child': {
                        borderBottom: `1px solid ${colors.accordion.border}`,
                      },
                      cursor: 'pointer',
                    }}
                    onClick={() => onChangeShip && onChangeShip(ship.shipId)}
                  >
                    <Box
                      sx={{
                        minHeight: 34,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        paddingLeft: theme.spacing(1),
                        paddingRight: theme.spacing(1),
                      }}
                    >
                      <Typography variant="body2" noWrap sx={{ paddingRight: theme.spacing(1) }}>
                        {ship.name}
                      </Typography>
                      <Radio
                        size="small"
                        color="primary"
                        checked={isSelected}
                        onChange={() => onChangeShip && onChangeShip(ship.shipId)}
                        onClick={(event) => event.stopPropagation()}
                        value={ship.shipId}
                      />
                    </Box>
                  </Box>
                );
              })}
            {ships.length === 0 && (
              <Typography variant="h5" sx={{ margin: theme.spacing(1) }}>
                {intl.formatMessage({ id: msgId.noResults })}
              </Typography>
            )}
          </CustomScrollBars>
          {onOkClick && (
            <Box sx={{ padding: theme.spacing(1), textAlign: 'center' }}>
              <NormalButton
                label={intl.formatMessage({ id: msgId.decision })}
                color="primary"
                startIcon={<CheckCircle />}
                disabled={selectedShipId == null}
                onClick={onOkClick}
              />
            </Box>
          )}
        </React.Fragment>
      )}
      {onChangeOpen != null && !open && (
        <React.Fragment>
          <ContentsHeader title={intl.formatMessage({ id: msgId.shipSelection })} vertical={true}>
            {onChangeOpen != null && (
              <CloseIconButton onClick={() => onChangeOpen(true)}>
                <ChevronRightIcon />
              </CloseIconButton>
            )}
          </ContentsHeader>
        </React.Fragment>
      )}
    </SubDrawerBox>
  );
}

ShipDrawer.defaultProps = {
  open: true,
  showCalendar: false,
};
